import { ReactNode, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';

interface Props {
  icon: ReactNode;
  count: number;
  ariaLabel: string;
  /** Optional wrapper title; omit when the embedded panel owns its own header. */
  panelTitle?: string;
  children: (close: () => void) => ReactNode;
}

export default function NotificationBell({
  icon,
  count,
  ariaLabel,
  panelTitle,
  children,
}: Props) {
  const [open, setOpen] = useState(false);
  const display = count > 9 ? '9+' : String(count);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="gt-bell-button"
          style={{
            position: 'relative',
            background: 'transparent',
            border: 0,
            padding: 6,
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          {icon}
          {count > 0 && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: 'var(--gc-primary, #1f4d3f)',
                color: 'white',
                fontSize: 10,
                lineHeight: '16px',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {display}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          style={{
            width: 380,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'min(620px, 85vh)',
            background: 'var(--gt-surface-1)',
            border: '1px solid var(--gt-border)',
            borderRadius: 16,
            boxShadow: '0 16px 40px rgba(6, 24, 18, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 50,
          }}
        >
          {panelTitle && (
            <div
              style={{
                padding: '12px 14px',
                fontWeight: 600,
                fontSize: 14,
                borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
              }}
            >
              {panelTitle}
            </div>
          )}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {children(() => setOpen(false))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
